import React, { useContext } from 'react';

import { Box } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import { AxiosResponse } from 'axios';

import { UserPreview } from '@vyce/core/src/views/preview/user/UserPreview';
import {
  getEmployeePreviewRequest,
  getHealthAndSafetyPDFByUserIdRequest,
  getPassportRequest,
  getRtwRequest,
} from '@vyce/core/src/api/pay';
import { DeviceContext } from '@vyce/core/src/contexts';
import { useEmployeeProfileData } from '@vyce/core/src/modules/userPreviewModule/hooks/useEmployeeProfileData';
import { useOnboardingRecompute } from '@vyce/core/src/modules/userPreviewModule/hooks/useOnboardingRecompute';
import { getWorkerOnboardingRequest } from '@vyce/core/src/api/onboarding';
import { PageLoading } from '@vyce/core/src/components';
import { deleteCustomQualificationRequest, getCheckersRequest } from '@vyce/core/src/api/checkers';
import { QualificationDialog } from '@vyce/core/src/modules/qualificationDialog';
import {
  createCustomQualificationRequest,
  updateQualificationRequest,
  createQualificationRequest,
  uploadCustomQualificationImageRequest,
  deleteCustomQualificationImageRequest,
} from '@vyce/core/src/api/teams';
import { getTagListRequest } from '@vyce/core/src/api/handbook';
import { UserPreviewProvider } from '@vyce/core/src/contexts/userPreviewProvider';

import { useTypedSelector } from '../../../hooks';
import { ChatUser, CustomCertificate, User } from '@vyce/core/src/types';
import useChatRequests from '@vyce/core/src/hooks/useChatRequests';
import { GetUserData } from '@vyce/core/src/api/types';

interface Props {
  /** Defaults to the legacy pay route; user-management injects the gateway `/core` request. */
  getUserRequest?: (params: GetUserData) => Promise<AxiosResponse>;
}

export const EmployeePreview: React.FC<Props> = ({ getUserRequest = getEmployeePreviewRequest }) => {
  const { selectedCompany } = useTypedSelector(state => state.helper);
  const {
    userPreviewData,
    userPreviewDataLoading,
    rtw,
    passport,
    userId,
    selectedCompanyId,
    hasPermissionForCreateQualification,
    isShowAgencyData,
    contextOptions,
    getUserPreviewData,
    workerOnboarding,
    refetchWorkerOnboarding,
  } = useEmployeeProfileData({
    getEmployeePreviewRequest: getUserRequest,
    getPassportRequest,
    getRtwRequest,
    getOnboardingRequest: getWorkerOnboardingRequest,
    selectedCompany,
  });
  const { recomputeOnboarding, recomputeOnboardingLoading } = useOnboardingRecompute({
    companyId: selectedCompany?.uuid,
    userId,
    onboarding: workerOnboarding,
    refetch: refetchWorkerOnboarding,
  });
  const { isMobile } = useContext(DeviceContext);
  const history = useHistory();
  const { first_name, last_name, email, phone, photo, uuid } = useTypedSelector(state => state.user);
  const { createPrivateChat } = useChatRequests();
  const chatCreator: ChatUser = {
    uid: uuid,
    name: `${first_name} ${last_name}`,
    email,
    phone,
    avatarUrl: photo?.url,
  };

  const goToChat = async (userData: User) => {
    if (!chatCreator || !selectedCompany?.uuid) {
      return;
    }
    await createPrivateChat({
      creator: chatCreator,
      chatUser: {
        uid: userData.uuid,
        name: `${userData.first_name} ${userData.last_name}`,
        email: userData.email,
        phone: userData.phone,
        avatarUrl: userData.photo?.url,
      },
      companyId: selectedCompany?.uuid,
    });
  };

  if (userPreviewDataLoading)
    return (
      <Box height="100%">
        <PageLoading />
      </Box>
    );

  return (
    <Box padding={isMobile ? 1 : 5}>
      <UserPreviewProvider value={contextOptions}>
        {userPreviewData && (
          <UserPreview
            rtw={rtw}
            passport={passport}
            createPrivateChat={() => goToChat(userPreviewData.user)}
            timesheetsLink={`${history.location.pathname}/timesheets`}
            paymentsLink={`${history.location.pathname}/payments`}
            agreementsLink={`${history.location.pathname}/agreements`}
            userPreviewData={userPreviewData}
            companyId={selectedCompany?.uuid ?? ''}
            userId={userId}
            onboarding={workerOnboarding}
            onRecomputeOnboarding={recomputeOnboarding}
            recomputeOnboardingLoading={recomputeOnboardingLoading}
            deleteCustomQualificationRequest={deleteCustomQualificationRequest}
            showHealthAndSafetyPDFRequest={getHealthAndSafetyPDFByUserIdRequest}
            hasPermissionForCreateQualification={hasPermissionForCreateQualification}
            isShowAgencyData={isShowAgencyData}
            withAddQualificationModal
            renderQualificationModal={({
              isOpen,
              selectedQualification,
              onClose,
            }: {
              isOpen: boolean;
              selectedQualification: CustomCertificate;
              onClose: () => void;
            }) => (
              <QualificationDialog
                isAdmin={false}
                isOpen={isOpen}
                onClose={onClose}
                selectedQualification={selectedQualification as CustomCertificate}
                reRequestData={getUserPreviewData}
                getCheckersRequest={getCheckersRequest}
                triggerCheckerRequest={createQualificationRequest}
                createCustomQualificationRequest={createCustomQualificationRequest}
                updateCustomQualificationRequest={updateQualificationRequest}
                uploadCustomQualificationImageRequest={uploadCustomQualificationImageRequest}
                deleteCustomQualificationImageRequest={deleteCustomQualificationImageRequest}
                getTagListRequest={getTagListRequest}
                type="outside"
                userId={userId}
                selectedCompanyId={selectedCompanyId}
              />
            )}
          />
        )}
      </UserPreviewProvider>
    </Box>
  );
};
